import React from 'react';
import { Box, Container, Typography, Grid, LinearProgress } from '@mui/material';
import { styled } from '@mui/material/styles';

// Styled components
const SectionWrapper = styled(Box)(({ theme }) => ({
  backgroundImage: 'url("assets/images/bg/choose-bg.jpg")',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  padding: `${theme.spacing(12)} 0`,
  position: 'relative',
  // Background overlay removed to show the actual background image
  [theme.breakpoints.down('md')]: {
    padding: `${theme.spacing(7)} 0`,
  }
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  fontWeight: 700,
  position: 'relative',
  paddingBottom: theme.spacing(3),
  '&::before': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: 100,
    height: 4,
    backgroundColor: theme.palette.primary.main
  }
}));

const SkillbarTitle = styled(Typography)(({ theme }) => ({
  display: 'block',
  marginBottom: theme.spacing(1),
  fontWeight: 500,
}));

const StyledLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 10,
  borderRadius: 0,
  marginBottom: theme.spacing(3),
  backgroundColor: '#e1e1e1',
  '& .MuiLinearProgress-bar': {
    backgroundColor: theme.palette.primary.main,
  }
}));

const ServiceItem = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: theme.palette.background.dark,
  color: theme.palette.text.light,
  borderRadius: 0,
  transition: 'all 0.3s ease',
  height: '100%',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 10px 20px rgba(0,0,0,0.2)',
  }
}));

const ServiceIcon = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  '& img': {
    height: 60,
    width: 'auto',
  }
}));

const VideoWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  '& img': {
    width: '100%',
    height: 'auto',
    display: 'block',
  }
}));

const WhyChoose = () => {
  const skills = [
    { title: 'Success rate in repelling cyber attacks', percent: 98 },
    { title: 'Client Security Compliance Posture', percent: 97 },
    { title: 'Improvement in clients\' security posture within the first year', percent: 75 },
    { title: 'Reduction in Security-Related Downtime', percent: 65 }
  ];

  const services = [
    {
      icon: '/assets/images/choose/icons/1.png',
      title: 'Expert professionals',
      description: 'Expertise at your service, safeguarding your business'
    },
    {
      icon: '/assets/images/choose/icons/2.png',
      title: 'Customer Experience',
      description: 'Delivering trust with every interaction'
    },
    {
      icon: '/assets/images/choose/icons/3.png',
      title: 'Committed to quality',
      description: 'Your peace of mind is our promise of quality'
    }
  ];

  return (
    <SectionWrapper id="why-choose" className="rs-choose">
      <Box sx={{ position: 'relative', zIndex: 2 }}>
        <Container>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6} sx={{ pr: { lg: 8, md: 2, xs: 0 }, mb: { xs: 6, md: 0 } }}>
              <Box className="sec-title" sx={{ mb: 4 }}>
                <SectionTitle variant="h2">
                  Why Partnering with Us
                </SectionTitle>
                <Typography variant="body1" sx={{ mt: 2 }}>
                  Choosing us means opting for unmatched cybersecurity excellence. We're a
                  team of certified professionals committed to defending your digital landscape against
                  the most sophisticated threats
                </Typography>
              </Box>
              
              <Box sx={{ mt: 4 }} className="rs-skillbar">
                {skills.map((skill, index) => (
                  <Box key={index} sx={{ mb: 3 }}>
                    <SkillbarTitle variant="body1">{skill.title}</SkillbarTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <StyledLinearProgress 
                        variant="determinate" 
                        value={skill.percent} 
                        sx={{ 
                          flex: 1,
                          mb: index === skills.length - 1 ? 0 : 3 
                        }}
                      />
                      <Typography variant="body2" sx={{ ml: 1, fontWeight: 'bold' }}>
                        {skill.percent}%
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <VideoWrapper className="rs-videos choose-video">
                <img 
                  src="/assets/images/choose/chosse-video.jpg" 
                  alt="Why Choose Us" 
                />
              </VideoWrapper>
            </Grid>
          </Grid>
        </Container>

        <Box sx={{ pt: 12, mt: { xs: 6, md: 0 } }} id="rs-services" className="rs-services chooseus-style">
          <Container>
            <Grid container spacing={3}>
              {services.map((service, index) => (
                <Grid item xs={12} md={4} key={index} sx={{ mb: { xs: 3, md: 0 } }}>
                  <ServiceItem className="services-item">
                    <ServiceIcon className="services-icon">
                      <img src={service.icon} alt={service.title} />
                    </ServiceIcon>
                    <Box className="services-text">
                      <Typography variant="h5" sx={{ color: 'white', mb: 1 }}>
                        {service.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                        {service.description}
                      </Typography>
                    </Box>
                  </ServiceItem>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      </Box>
    </SectionWrapper>
  );
};

export default WhyChoose;
